import type { BookingsFilter, BookingStatus, ChartGroup, ChartSeriesSpec } from './types';
// Type-only import (erased at runtime) so the common/StatusPill → constants
// dependency stays acyclic while we borrow the FilterControl option shape.
import type { FilterOption } from '../common/FilterControl';

// Area display labels are canonical in the schema package (one source shared with
// the API). Re-exported here so the bookings domain reads every area constant from
// one place — no component reaches into @bbbk/schema for a label on its own.
export { AREA_LABELS } from '@bbbk/schema';
import { AREA_RATES, DEFAULT_AREA_RATE, ONE_NIGHT_RATE, VIEWING_STATUSES, VIEWING_STATUS_LABELS, type ViewingStatus } from '@bbbk/schema';

// The cleared bookings filter — one source, shared by the page (initial state)
// and the view (the "clear filters" reset), so a new filter field can't be
// forgotten in one of them.
export const EMPTY_BOOKINGS_FILTER: BookingsFilter = { q: null, status: null, area: null, inOn: null, types: [], window: null };

// The Type filter (viewings-in-the-bookings-list AC9): single-select like
// Status, with an All ROW — one of three, never empty (Sir, 2026-09-02). The
// pill reads All uncoloured, as Status's unset state does; the menu marks the
// All row chosen.
export const TYPE_ALL = 'all';
export const TYPE_OPTIONS: FilterOption[] = [
  { key: TYPE_ALL, label: 'All', icon: 'list' },
  { key: 'booking', label: 'Booking', icon: 'calendar' },
  { key: 'viewing', label: 'Viewing', icon: 'eye' },
];

// Carried verbatim from _prototype/bookings.jsx BOOKING_STATUSES / KENNEL_AREAS.
// The single source of status display across the whole app — every status label
// everywhere resolves through here (via STATUS_BY_KEY / the shared StatusPill).
// Keys are the persisted values; only labels are presentational. A deposit-paid
// booking reads as "Confirmed"; a deposit-less one reads as "Unconfirmed".
// `icon` is a unique, self-evident glyph per status (Icon-component name), shown
// in place of the old dot in the status badge and the status filter chips.
export const BOOKING_STATUSES: { key: BookingStatus; label: string; tone: string; icon: string }[] = [
  // Icons are dedicated to statuses (not shared with nav/other UI): hourglass
  // (awaiting), circle-check (sealed), and log-in / log-out as exact mirrors
  // for arrival vs collection.
  // `tone` is the status's own colour class (see the --st-* tokens + the
  // .status-pill / .filter-chip rules), so each status reads its distinct hue.
  { key: 'unconfirmed', label: 'Unconfirmed', tone: 'unconfirmed', icon: 'hourglass' }, // yellow — awaiting deposit
  { key: 'confirmed', label: 'Confirmed', tone: 'confirmed', icon: 'circle-check' }, // terracotta — booked
  { key: 'arrived', label: 'Arrived', tone: 'arrived', icon: 'log-in' }, // teal — checked in (mirror of collected)
  { key: 'collected', label: 'Collected', tone: 'collected', icon: 'log-out' }, // grey — checked out
  { key: 'cancelled', label: 'Cancelled', tone: 'cancelled', icon: 'close' }, // red — cancelled
  // portal-foundations AC9. A stay a household ASKED for and nobody has
  // accepted yet — it precedes `unconfirmed` in time, and is listed last here
  // for the same reason STATUSES appends it: reordering would change every
  // control derived from this array. Nothing can reach it yet; the portal that
  // writes it does not exist. It is here so that when the first one lands, it
  // draws as a status rather than as an unstyled blank.
  // The CLOCK, as the prototype draws it (sd, sdq): waiting on a person. It
  // named `inbox`, which the icon set does not have, so the pill drew no glyph.
  { key: 'requested', label: 'Requested', tone: 'requested', icon: 'clock' }, // slate — asked for, not ours yet
  // Red like cancelled; the glyph carries the difference the colour does not
  // (prototype 16r). Never in a menu: SELECTABLE_STATUSES leaves it out.
  { key: 'declined', label: 'Declined', tone: 'declined', icon: 'circle-x' },
];

// The five a PERSON may choose. BOOKING_STATUSES above is the DISPLAY
// vocabulary — what a status renders as; this is the WRITE vocabulary — what a
// control may set. They differ by exactly one word, and the split exists
// because everything that lets somebody pick a status derives from one array:
// the filter chips, the modal's dropdown, and the click-to-cycle pill. Leaving
// `requested` in that array is what AC9 means by "not yet reachable by any
// action" — a dropdown offering it makes it reachable, and the desk would be
// able to put a stay into a state only a customer is supposed to create.
//
// `desk-requested-status` is the story that makes the word selectable, and it
// removes this filter rather than adding a second list.
export const SELECTABLE_STATUSES = BOOKING_STATUSES.filter((s) => s.key !== 'requested' && s.key !== 'declined');

export const STATUS_BY_KEY: Record<string, { key: BookingStatus; label: string; tone: string; icon: string }> =
  Object.fromEntries(BOOKING_STATUSES.map((s) => [s.key, s]));

// The status options in the shared FilterControl shape — key/label plus the
// status's real glyph tinted by its tone token. ONE definition, used by BOTH
// the bookings Status filter (BookingsToolbar) and the booking modal's status
// dropdown (BookingForm), so the two pickers can never drift.
// Where ONE dog goes, as a dropdown — the same FilterControl the Status pill
// uses (dogs-in-areas AC2). It was a segmented three-button control until
// 2026-09-11; at the modal's real width the three buttons left nothing for the
// dog's name and breed, which is the half of the row that identifies the dog.
// A pill collapses to its chosen label and gives the name its space back.
//
// THE MARKERS (Freddi, 2026-09-11: "replace round dot with suitable icons to
// represent the areas"). A coloured dot is not a marker, it is a legend entry —
// it means nothing until you have been told what it means, and nobody tells you.
//
// The three areas are three BUILDINGS in the yard — the kennel block, the shed,
// the house — and the desk already calls them that, so the glyph names the thing
// rather than symbolising it (Freddi: "use kennel icon for big, and shed for
// little"). A first pass drew one paw at two sizes and needed explaining, which
// is the same failure as the dot in a longer sentence.
// The tones are the app's settled area language — the same three the chart's
// AREA_SERIES uses — taken at their `-deep` end, which is what stays legible as
// a 14px stroke rather than a filled band.
//
// The glyph removed earlier the same day was the filter-TYPE icon on the pill —
// a paw sitting left of the word "Area", saying only what the word already said.
// That one is still gone. These carry the options, which is different work.
export const DOG_AREA_OPTIONS: FilterOption[] = [
  { key: 'big', label: 'Big', icon: 'kennel', dot: 'var(--primary-deep)' },
  { key: 'little', label: 'Little', icon: 'shed', dot: 'var(--secondary-deep)' },
  { key: 'house', label: 'House', icon: 'home', dot: 'var(--alert-deep)' },
];

const asOption = (s: (typeof BOOKING_STATUSES)[number]): FilterOption => ({
  key: s.key,
  label: s.label,
  icon: s.icon, // the app's real status glyph
  dot: `var(--st-${s.tone})`, // tints the icon in the status colour
});

// TWO LISTS, SPLIT BY WHAT THE CONTROL DOES (request-a-stay AC15, AC16).
//
// FILTERING finds a booking by the status it is in, and a customer's request
// is in `requested` — so the Bookings status filter offers it (R-DESK-13:
// "the existing status filter gains Requested").
//
// SETTING a status is the desk's own act, and the desk never makes a request
// (§3.1, decision 81): the booking modal's menu and the booking form take the
// pick list, which keeps it out. One array each, so the two can never blur.
export const STATUS_FILTER_OPTIONS: FilterOption[] = BOOKING_STATUSES.map(asOption);
export const STATUS_PICK_OPTIONS: FilterOption[] = SELECTABLE_STATUSES.map(asOption);

/** One status as a READ-ONLY option — the disabled Status control on a request or a declined booking shows its word; nothing can pick it. */
export const statusShown = (key: BookingStatus): FilterOption[] => (STATUS_BY_KEY[key] ? [asOption(STATUS_BY_KEY[key]!)] : []);

// The lifecycle cycle order, derived from BOOKING_STATUSES so there is one
// source of truth. Used by both the BookingModal status pill and the bookings-
// table inline status toggle.
export const STATUS_ORDER: BookingStatus[] = SELECTABLE_STATUSES.map((s) => s.key);

// The next status when the pill is clicked: unconfirmed → confirmed → arrived →
// collected → cancelled → unconfirmed. An unknown status restarts at the first.
export function nextStatus(status: BookingStatus): BookingStatus {
  const i = STATUS_ORDER.indexOf(status);
  return STATUS_ORDER[(i + 1) % STATUS_ORDER.length]!;
}

// The bookable areas the form offers, with the kennel counts behind the stat
// strip. RATES ARE NOT DECLARED HERE — they come from AREA_RATES in
// @bbbk/schema (portal-foundations AC8), so the desk's chip and the price the
// customer portal quotes read one number. `label` and `capacity` stay: they are
// display and occupancy, which no portal surface has an opinion about.
export const KENNEL_AREAS: { key: string; label: string; capacity: number; rate: number }[] = [
  { key: 'big', label: 'Big', capacity: 12, rate: AREA_RATES.big! },
  { key: 'little', label: 'Little', capacity: 8, rate: AREA_RATES.little! },
  // FOUR CHIPS since dogs-in-areas (Freddi, 2026-09-10): Big, Little, House,
  // Other. `house` is a peer rather than something reached through Other —
  // 82.6% of bookings carry a single dog, so the commonest way to use the house
  // would otherwise have been to open a per-dog panel to place one dog.
  //
  // `other` carries no rate or capacity of its own: it is DERIVED when the dogs
  // disagree, and each of them is priced and counted where it actually is.
  { key: 'house', label: 'House', capacity: 4, rate: AREA_RATES.house ?? DEFAULT_AREA_RATE },
  { key: 'other', label: 'Other', capacity: 0, rate: DEFAULT_AREA_RATE },
];

// Re-exported from the moved pricing module for the handful of call sites that
// read it by this name.
export { ONE_NIGHT_RATE };

export const TOTAL_CAPACITY = KENNEL_AREAS.reduce((s, a) => s + a.capacity, 0);

// Chart status series — fill + line tokens per status (prototype STATUS_SERIES).
export const STATUS_SERIES: ChartSeriesSpec[] = [
  { key: 'unconfirmed', label: 'Unconfirmed', fill: 'var(--warn)', line: 'var(--warn-deep)' },
  { key: 'confirmed', label: 'Confirmed', fill: 'var(--secondary)', line: 'var(--secondary-deep)' },
  { key: 'arrived', label: 'Arrived', fill: 'var(--primary)', line: 'var(--primary-deep)' },
  { key: 'collected', label: 'Collected', fill: 'var(--ink-mute)', line: 'var(--ink-mute)' },
  { key: 'cancelled', label: 'Cancelled', fill: 'var(--alert)', line: 'var(--alert-deep)' },
  { key: 'requested', label: 'Requested', fill: 'var(--st-requested-soft)', line: 'var(--st-requested)' },
  { key: 'declined', label: 'Declined', fill: 'var(--st-declined-soft)', line: 'var(--st-declined)' },
];

// Chart area series — one band per kennel area (mirrors STATUS_SERIES). All four
// areas in the data; per-day totals stay honest across the breakdown.
export const AREA_SERIES: ChartSeriesSpec[] = [
  { key: 'big', label: 'Big', fill: 'var(--primary)', line: 'var(--primary-deep)' },
  { key: 'little', label: 'Little', fill: 'var(--secondary)', line: 'var(--secondary-deep)' },
  { key: 'house', label: 'House', fill: 'var(--alert)', line: 'var(--alert-deep)' },
  // `mix` retired for `other` (dogs-in-areas AC4). The band keeps the muted
  // tone: `other` is the absence of agreement, not a place, and it should not
  // read as loudly as the three real areas beside it.
  { key: 'other', label: 'Other', fill: 'var(--ink-mute)', line: 'var(--ink-mute)' },
];

// The chart's group-by dimension selector (Status / Area). Each carries the app's
// own glyph so the filter reads like every other one (status = the status tick,
// area = the house/home).
export const CHART_GROUPS: { key: ChartGroup; label: string; icon: string }[] = [
  { key: 'status', label: 'By status', icon: 'circle-check' },
  { key: 'area', label: 'By area', icon: 'home' },
];

// The Daily page's group list. 'By money' is offered only to the roles that can
// see Finances at all (routeMap NAV gives it to admin + owner): helpers work the
// desk but do not see the takings, and /api/finances/* 403s them regardless.
export const DAILY_CHART_GROUPS: { key: ChartGroup; label: string; icon: string }[] = [
  ...CHART_GROUPS,
  { key: 'money', label: 'By money', icon: 'wallet' },
];

// v1 chart types — 'gantt' deliberately dropped (registry "Gantt NOT registered").
export const CHART_TYPES: { key: 'line' | 'bar'; label: string; icon: string }[] = [
  { key: 'line', label: 'Occupancy line', icon: 'line' },
  { key: 'bar', label: 'Status bars', icon: 'bar' },
];

// First-click sort direction per column (prototype NATURAL_DIR).
export const NATURAL_DIR: Record<string, 'asc' | 'desc'> = {
  dog: 'asc',
  breed: 'asc',
  area: 'asc',
  stay: 'desc',
  total: 'desc',
  status: 'asc',
  updated: 'desc',
  added: 'desc',
};

// --- viewings ---------------------------------------------------------------

// The viewing statuses in the SAME { key, label, tone, icon } shape as
// BOOKING_STATUSES, so the shared StatusPill needs only to be handed a resolved
// record rather than rewritten. Labels and vocabulary are canonical in
// @bbbk/schema; the tone and the glyph are presentational and live here beside
// their booking counterparts.
//
// TONES ARE THEIR OWN. `expected` sits on the viewing accent rather than
// borrowing `unconfirmed` — StatusPill applies the tone verbatim as a CSS
// class, so borrowing would paint a viewing the booking-status gold and quietly
// tie two vocabularies together that are separate by design. Only `visited`
// borrows an existing palette (collected's greys, since it means the same
// thing: settled and behind you), and `no-show` borrows --alert.
export const VIEWING_STATUS_RECORDS: Record<ViewingStatus, { key: ViewingStatus; label: string; tone: string; icon: string }> = {
  expected: { key: 'expected', label: VIEWING_STATUS_LABELS.expected, tone: 'expected', icon: 'eye' },
  visited: { key: 'visited', label: VIEWING_STATUS_LABELS.visited, tone: 'visited', icon: 'circle-check' },
  no_show: { key: 'no_show', label: VIEWING_STATUS_LABELS.no_show, tone: 'no-show', icon: 'close' },
};

// The same three in the shared FilterControl shape — the modal's status
// dropdown. ONE definition, so the desk pill and the dropdown can never drift.
// A dropdown rather than click-to-cycle, deliberately: the app chose that
// everywhere "so a status can't be toggled past the one you meant."
export const VIEWING_STATUS_OPTIONS: FilterOption[] = VIEWING_STATUSES.map((key) => ({
  key,
  label: VIEWING_STATUS_RECORDS[key].label,
  icon: VIEWING_STATUS_RECORDS[key].icon,
  dot: `var(--st-${VIEWING_STATUS_RECORDS[key].tone})`,
}));
